import { useMemo } from 'react'

import { MediaTypes, SocialSubmissionTypes, SubmissionChannels } from '@enums'
import { IPostPurchaseBrandConnectAnalytics } from '@store/postPurchaseAnalytics'
import { IAnalyticsBreakdownFilterProps } from './Filter/AnalyticsBreakdownFilter'
import { AnalyticsBrandConnectBreakdown } from './BrandConnectBreakdown'
import { MetricEntry } from './types'
import { usePostPurchaseBrandConnectMetricBreakdown } from './useAnalyticsBreakdown'

type Props = {
  analytics: IPostPurchaseBrandConnectAnalytics[]
  socialSubmissionType: SocialSubmissionTypes
  currency: string
  isLoaded: boolean
  isProxy?: boolean
  channel?: SubmissionChannels
  mediaType?: MediaTypes
} & IAnalyticsBreakdownFilterProps

export const AnalyticsBrandConnectPostPurchaseBreakdown: React.FC<Props> = ({
  analytics,
  filters,
  ...props
}) => {
  const filteredAnalytics = useMemo(() => {
    const filterEntries = Object.entries(filters || {})

    if (filterEntries.length === 0) {
      return analytics
    }

    return analytics.filter((a) =>
      filterEntries.every(
        ([key, filter]) =>
          a[key as keyof IPostPurchaseBrandConnectAnalytics] === filter.selected.value,
      ),
    )
  }, [analytics, filters])

  const analyticsData = filteredAnalytics.length > 0 ? filteredAnalytics[0] : {}

  const { overview, viewerPerformance, engagement } = usePostPurchaseBrandConnectMetricBreakdown(
    analyticsData,
  )

  // Note: post-purchase glossary never included shopping/watch interest entries (unlike pre-purchase).
  const manualGlossaryEntries: MetricEntry[] = [
    { metric: 'audience_age', source: 'youtube_brand_connectV2.post_purchase', value: 0 },
    { metric: 'audience_gender', source: 'youtube_brand_connectV2.post_purchase', value: 0 },
    { metric: 'audience_location', source: 'youtube_brand_connectV2.post_purchase', value: 0 },
  ]

  return (
    <AnalyticsBrandConnectBreakdown
      {...props}
      analytics={analytics}
      filters={filters}
      sections={[
        { title: 'overview', entries: overview },
        { title: 'viewerPerformance', entries: viewerPerformance },
        { title: 'engagementBreakdown', entries: engagement },
      ]}
      customGlossaryEntries={manualGlossaryEntries}
      perspective="post-purchase"
    />
  )
}
